import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../teacher/CLOs.css';
import '../teacher/Courses.css';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin);

const DepartmentReports = () => {
    const handleCourseClick = async (courseId) => {
        // Toggle behaviour: deselect if clicking the same course again
        if (selectedCourseId === courseId) {
            setSelectedCourseId(null);
            setCourseStudentCodes(null);
            setCourseTools(null);
            return;
        }
        try {
            const res = await api.get(`/admin/courses/${courseId}/grades`);
            if (res.data.success) {
                const codes = res.data.data.students.map(s => s.student_code.toString());
                setCourseStudentCodes(new Set(codes));
                setSelectedCourseId(courseId);
                setCourseTools(res.data.data.assessmentTools);
                setCourseStudents(res.data.data.students);
            }
        } catch (e) {
            console.error('Error fetching course students:', e);
        }
    };
    const { t, i18n } = useTranslation();
    const isAr = i18n.language === 'ar';
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);

    // Selection state
    const [selectedDeptId, setSelectedDeptId] = useState('');

    // Data state
    const [reportData, setReportData] = useState(null);
    // Filter states
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [courseStudentCodes, setCourseStudentCodes] = useState(null);
    const [courseTools, setCourseTools] = useState(null);
    const [courseStudents, setCourseStudents] = useState(null);
    const [deptCourses, setDeptCourses] = useState([]);
    const [fetching, setFetching] = useState(false);

    // Modal states
    const [showCLOMatrixModal, setShowCLOMatrixModal] = useState(false);
    const [showCLOFormModal, setShowCLOFormModal] = useState(false);
    const [showMarksModal, setShowMarksModal] = useState(false);
    const [showStudentGradesModal, setShowStudentGradesModal] = useState(false);
    const chartRef = useRef(null);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const deptRes = await api.get('/admin/departments');
            if (deptRes.data.success) setDepartments(deptRes.data.data);
        } catch (error) {
            console.error('Error fetching initial data:', error);
        } finally {
            setLoading(false);
        }
    };

    // CLO chart data (percentages per CLO)
    // Derived students list (filtered by selected course if any)
    const students = useMemo(() => {
        if (!reportData || !reportData.students) return [];
        if (selectedCourseId && courseStudents) return courseStudents;
        if (!courseStudentCodes) return reportData.students;
        return reportData.students.filter(s => courseStudentCodes.has(s.student_code.toString()));
    }, [reportData, courseStudentCodes, selectedCourseId, courseStudents]);

    // Choose tools list (filtered by course if selected)
    const assessmentTools = useMemo(() => {
        let list = selectedCourseId && courseTools ? courseTools : (reportData ? reportData.assessmentTools : []);
        return list.map(t => {
            const nameEn = t.name_en || t.tool_name_en || '';
            const nameAr = t.name_ar || t.tool_name_ar || '';
            const key = t.key || t.tool_name_en || t.tool_name_ar || `tool_${t.tool_id}`;
            return {
                ...t,
                key,
                questions: (t.questions || []).map(q => ({
                    ...q,
                    max: q.max !== undefined ? q.max : (q.max_score !== undefined ? parseFloat(q.max_score) : 0),
                    type: q.type || q.question_type || ''
                }))
            };
        });
    }, [selectedCourseId, courseTools, reportData]);

    // Alias for clarity
    const filteredStudents = students;

    const cloChartData = useMemo(() => {
        if (!reportData) return null;
        const domains = { 'Knowledge': ['K1', 'K2', 'K3', 'K4', 'K5'], 'Skills': ['S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8'], 'Values': ['V1', 'V2', 'V3', 'V4', 'V5'] };
        const labels = []; const values = []; const colors = [];
        reportData.assessmentTools.forEach(t => { t.questions = t.questions || []; });
        const getQuestionActual = (student, tool, qIdx) => {
            return student.tools?.[tool.key]?.[qIdx] || 0;
        };
        Object.values(domains).flat().forEach(type => {
            let totalMax = 0; let scaledTotalAvg = 0;
            reportData.assessmentTools.forEach(tool => {
                tool.questions.forEach(q => {
                    const qType = q.type || q.question_type || '';
                    if (qType.toUpperCase() === type) {
                        totalMax += parseFloat(q.max || 0);
                        scaledTotalAvg += parseFloat(q.true_average || 0);
                    }
                });
            });
            if (totalMax > 0) {
                const perc = (scaledTotalAvg / totalMax) * 100;
                labels.push(type);
                const p = parseFloat(perc.toFixed(2));
                values.push(p);
                colors.push(p >= 70 ? '#16a34a' : '#dc2626');
            }
        });
        return {
            labels,
            datasets: [{ label: isAr ? 'المستوى الفعلي' : 'Actual Level', data: values, backgroundColor: colors }]
        };
    }, [reportData, isAr]);

    const currentDept = useMemo(() => {
        return departments.find(d => d.department_id === parseInt(selectedDeptId));
    }, [departments, selectedDeptId]);

    useEffect(() => {
        if (!selectedDeptId) {
            setReportData(null);
            return;
        }
        fetchDepartmentSummary(selectedDeptId);
        // fetch courses for department
        api.get('/admin/courses').then(r => {
            if (r.data?.success) {
                const list = r.data.data.filter(c => c.department_id === parseInt(selectedDeptId));
                setDeptCourses(list);
            }
        }).catch(() => { });
    }, [selectedDeptId]);

    const getCategoryFromTool = (tool) => {
        const name = (tool.name_en || tool.name_ar || tool.tool_name_en || tool.tool_name_ar || '').toUpperCase();
        if (name.includes('MD1') || name.includes('MIDTERM 1') || name.includes('MID 1') || name.includes('النصفي الأول') || name.includes('نصفي 1')) return 'md1';
        if (name.includes('MD2') || name.includes('MIDTERM 2') || name.includes('MID 2') || name.includes('النصفي الثاني') || name.includes('نصفي 2')) return 'md2';
        if (name.includes('LAB') || name.includes('العملي') || name.includes('عملي')) return 'lab';
        if (name.includes('FINAL') || name.includes('FNL') || name.includes('النهائي') || name.includes('نهائي')) return 'final';
        if (name.includes('HM') || name.includes('HOMEWORK') || name.includes('الواجب') || name.includes('واجبات')) return 'hm';
        if (name.includes('QZ') || name.includes('QUIZ') || name.includes('كويز') || name.includes('الاختبار القصير')) return 'qz';
        // Categorize anything else as homework/catch-all
        return 'hm';
    };

    const getMaxByCategory = (category) => {
        if (!reportData) return 0;
        let total = 0;
        reportData.assessmentTools.forEach(tool => {
            if (getCategoryFromTool(tool) === category) {
                tool.questions.forEach(q => {
                    total += parseFloat(q.max || 0);
                });
            }
        });
        return +total.toFixed(2);
    };

    const getToolName = (type) => {
        const mapping = {
            'HM': isAr ? 'الواجبات' : 'Homework', 'QZ': isAr ? 'الاختبارات' : 'Quizzes',
            'MD1': isAr ? 'نصفي 1' : 'Midterm 1', 'MD2': isAr ? 'نصفي 2' : 'Midterm 2',
            'LAB': isAr ? 'العملي' : 'Lab', 'FNL': isAr ? 'النهائي' : 'Final Exam'
        };
        return mapping[type] || type;
    };

    const fetchDepartmentSummary = async (deptId) => {
        try {
            setFetching(true);
            const res = await api.get(`/admin/departments/${deptId}/summary`);
            if (res.data.success) {
                const data = res.data.data;

                // Add course info for display
                data.course = {
                    course_name_ar: `قسم ${data.department.department_name_ar}`,
                    course_name_en: `${data.department.department_name_en} Dept`,
                    course_code: 'DEPT',
                    semester: isAr ? 'فصل مجمع' : 'Aggregated'
                };

                setReportData(data);
            }
        } catch (e) {
            console.error('Error fetching summary:', e);
        } finally {
            setFetching(false);
        }
    };

    const getStudentTotal = (scode) => {
        if (!assessmentTools || !students) return 0;
        const student = students.find(s => s.student_code === scode);
        if (!student) return 0;

        let total = 0;
        if (selectedCourseId) {
            assessmentTools.forEach(t => {
                const toolScores = t.gradesByStudent?.[student.student_id] || {};
                t.questions.forEach(q => {
                    const val = toolScores[q.question_id];
                    if (val !== undefined && val !== null && val !== '') {
                        total += parseFloat(val) || 0;
                    }
                });
            });
        } else {
            if (!student.tools) return 0;
            assessmentTools.forEach(t => {
                const toolScores = student.tools[t.key] || {};
                t.questions.forEach((_, idx) => {
                    const val = toolScores[idx];
                    if (val !== undefined && val !== null && val !== '') {
                        total += parseFloat(val) || 0;
                    }
                });
            });
        }
        return total;
    };

    const getQuestionAverage = (toolKey, qIndex, questionId) => {
        if (!assessmentTools || !students.length) return 0;
        let sum = 0;

        if (selectedCourseId) {
            const tool = assessmentTools.find(t => t.key === toolKey);
            if (!tool || !tool.gradesByStudent) return 0;
            students.forEach(s => {
                const score = tool.gradesByStudent[s.student_id]?.[questionId];
                if (score !== undefined && score !== null && score !== '') {
                    sum += parseFloat(score);
                }
            });
            return students.length > 0 ? sum / students.length : 0;
        } else {
            const tool = assessmentTools.find(t => t.key === toolKey);
            return tool?.questions?.[qIndex]?.true_average || 0;
        }
    };

    const calculateGrade = (percentage) => {
        const s = Math.round(percentage);
        if (s >= 95) return 'A+'; if (s >= 90) return 'A'; if (s >= 85) return 'B+'; if (s >= 80) return 'B';
        if (s >= 75) return 'C+'; if (s >= 70) return 'C'; if (s >= 65) return 'D+'; if (s >= 60) return 'D';
        return 'F';
    };

    // Excel Utilities
    const applyHeaderStyle = (cell, isMain = true) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isMain ? 'FF1E293B' : 'FFF1F5F9' } };
        cell.font = { color: { argb: isMain ? 'FFFFFFFF' : 'FF1E293B' }, bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    };
    const applyCellStyle = (cell, isBold = false) => {
        cell.font = { bold: isBold, size: 10 };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    };


    const fillCLOMatrixSheet = (sheet) => {
        // Flatten questions for header
        const allCols = [];
        assessmentTools.forEach(t => {
            t.questions.forEach((q, idx) => {
                allCols.push({
                    ...q,
                    toolName: t.name_en || t.name_ar, // Prefer English
                    toolKey: t.key,
                    qIndex: idx,
                    parentTool: t
                });
            });
        });

        // Add headers
        sheet.addRow(['#', 'Code', 'Name', ...allCols.map(c => c.toolName), 'Total']).eachCell(c => applyHeaderStyle(c));
        sheet.addRow(['', '', 'Max Score', ...allCols.map(c => c.max), 100]).eachCell(c => applyHeaderStyle(c, false));
        // average based on filteredStudents only
        const avgRowData = ['', '', 'Average'];
        allCols.forEach(col => {
            if (selectedCourseId) {
                let sum = 0;
                filteredStudents.forEach(st => {
                    const score = col.parentTool.gradesByStudent?.[st.student_id]?.[col.question_id];
                    sum += parseFloat(score || 0);
                });
                const avg = filteredStudents.length ? (sum / filteredStudents.length) : 0;
                avgRowData.push(avg.toFixed(2));
            } else {
                avgRowData.push((col.true_average || 0).toFixed(2));
            }
        });
        avgRowData.push('');
        sheet.addRow(avgRowData).eachCell(c => applyHeaderStyle(c, false));
        sheet.addRow(['', '', 'PLOs', ...allCols.map(c => c.type), '']).eachCell(c => applyHeaderStyle(c, false));

        // Add students
        filteredStudents.forEach((s, i) => {
            const row = [i + 1, s.student_code, s.full_name_en || s.full_name_ar]; // Prefer English Name
            let visTotal = 0;
            allCols.forEach(col => {
                const score = selectedCourseId && col.parentTool.gradesByStudent
                    ? col.parentTool.gradesByStudent[s.student_id]?.[col.question_id]
                    : s.tools?.[col.toolKey]?.[col.qIndex];
                row.push(score || 0);
                visTotal += parseFloat(score || 0);
            });
            row.push(visTotal.toFixed(2));
            sheet.addRow(row).eachCell((c, colN) => applyCellStyle(c, colN === row.length));
        });
    };

    const handleExportCLOMatrix = async () => {
        if (!reportData) return;
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('PLO Matrix');
        fillCLOMatrixSheet(sheet);
        const buf = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buf]), `PLO_Matrix_${reportData.department.department_name_en}.xlsx`);
    };

    const fillMarksDistributionSheet = (sheet) => {
        const cats = [
            { nameEn: 'Midterm I Exam', nameAr: 'الامتحان النصفي الأول', key: 'md1' },
            { nameEn: 'Midterm II Exam', nameAr: 'الامتحان النصفي الثاني', key: 'md2' },
            { nameEn: 'Lab Work', nameAr: 'العملي', key: 'lab' },
            { nameEn: 'Class Work (Quizzes, Assignments, etc.)', nameAr: 'أعمال الفصل (واجبات، كويزات، إلخ)', key: 'class' },
            { nameEn: 'Final Exam', nameAr: 'الامتحان النهائي', key: 'final' }
        ];

        // Header Rows
        const sheetHead = [
            isAr ? 'م' : 'No.',
            isAr ? 'الرقم الجامعي' : 'Student ID',
            isAr ? 'الاسم' : 'Student Name',
            ...cats.map(c => isAr ? c.nameAr : c.nameEn),
            isAr ? 'المجموع' : 'Total'
        ];
        const hRow = sheet.addRow(sheetHead);
        hRow.eachCell(c => applyHeaderStyle(c));

        const maxScores = [
            '', '', isAr ? 'الدرجة القصوى' : 'Max Score',
            ...cats.map(c => {
                if (c.key === 'class') return +(getMaxByCategory('hm') + getMaxByCategory('qz')).toFixed(2);
                return getMaxByCategory(c.key);
            }),
            +(getMaxByCategory('md1') + getMaxByCategory('md2') + getMaxByCategory('lab') + getMaxByCategory('hm') + getMaxByCategory('qz') + getMaxByCategory('final')).toFixed(2)
        ];
        const maxRow = sheet.addRow(maxScores);
        maxRow.eachCell(c => applyHeaderStyle(c, false));

        reportData.students.forEach((s, i) => {
            const rowData = [i + 1, s.student_code, isAr ? s.full_name_ar : (s.full_name_en || s.full_name_ar)];

            rowData.push(getCategoryScore(s, 'md1'));
            rowData.push(getCategoryScore(s, 'md2'));
            rowData.push(getCategoryScore(s, 'lab'));
            rowData.push(+(getCategoryScore(s, 'hm') + getCategoryScore(s, 'qz')).toFixed(2));
            rowData.push(getCategoryScore(s, 'final'));

            rowData.push(+getStudentTotal(s.student_code).toFixed(2));

            const row = sheet.addRow(rowData);
            row.eachCell((c, colIdx) => applyCellStyle(c, colIdx === rowData.length));
        });

        // Column widths
        sheet.getColumn(1).width = 6;
        sheet.getColumn(2).width = 18;
        sheet.getColumn(3).width = 40;
        cats.forEach((_, idx) => { sheet.getColumn(4 + idx).width = 25; });
        sheet.getColumn(4 + cats.length).width = 15;
    };

    const handleExportMarksDistribution = async () => {
        if (!reportData) return;
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Marks Distribution');
        fillMarksDistributionSheet(sheet);
        const buf = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buf]), `Marks_Distribution_${reportData.department.department_name_en}.xlsx`);
    };

    const getCategoryScore = (student, categoryKey) => {
        if (!student || !student.tools) return 0;
        let total = 0;

        reportData.assessmentTools.forEach(tool => {
            if (getCategoryFromTool(tool) === categoryKey) {
                // Sum all question scores for this tool
                tool.questions.forEach((q, idx) => {
                    total += parseFloat(student.tools?.[tool.key]?.[idx] || 0);
                });
            }
        });
        return total;
    };

    const fillStudentGradesSheet = (sheet) => {
        // --- STUDENT GRADES SECTION ---
        const headers = ['ID', 'Name', 'Hm', 'Qz', 'Md1', 'Md2', 'Lab', 'Semester Works', 'Final', 'Total', 'Grade'];
        const hRow = sheet.addRow(headers);
        hRow.eachCell(c => applyHeaderStyle(c));

        reportData.students.forEach(s => {
            const hm = getCategoryScore(s, 'hm');
            const qz = getCategoryScore(s, 'qz');
            const md1 = getCategoryScore(s, 'md1');
            const md2 = getCategoryScore(s, 'md2');
            const lab = getCategoryScore(s, 'lab');

            const preFinal = hm + qz + md1 + md2 + lab;
            const fnl = getCategoryScore(s, 'final');
            const total = preFinal + fnl;

            // Calculate grade based on PERSONAL max percentage
            const pMax = s.total_personal_max || 100;
            const pct = (total / pMax) * 100;

            const row = [
                s.student_code,
                isAr ? s.full_name_ar : s.full_name_en,
                hm, qz, md1, md2, lab, preFinal, fnl,
                total.toFixed(2), calculateGrade(pct)
            ];

            const r = sheet.addRow(row);
            r.eachCell((c, colN) => applyCellStyle(c, colN === row.length - 1));
            if (calculateGrade(pct) === 'F') r.eachCell(c => c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE4E6' } });
        });

        // --- COURSE REPORT SECTION (APPENDED) ---
        sheet.addRow([]);
        sheet.addRow([]);

        const stats = reportData.gradeDistribution;
        const total = reportData.students.length;

        const getPercent = (val) => total > 0 ? ((val / total) * 100).toFixed(0) + '%' : '0%';
        const successPercent = total > 0 ? ((stats.Pass / total) * 100).toFixed(2) + '%' : '0.0%';

        // Success Rate
        const sRow = sheet.addRow(['Success Rate', successPercent]);
        sRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCFCE7' } };
        sRow.getCell(1).font = { bold: true };
        sRow.getCell(2).font = { bold: true };
        sheet.addRow([]);

        // Main Stats Table matching UI
        const grades = ['A+', 'A', 'B+', 'B', 'C+', 'C', 'D+', 'D', 'F', 'Denied Entry', 'In Progress', 'Incomplete', 'Pass', 'Fail', 'Withdrawn', 'Total'];
        const rhRow = sheet.addRow(grades);
        rhRow.eachCell(c => applyHeaderStyle(c));

        const counts = grades.map(g => {
            if (g === 'Total') return total;
            // Map UI labels to data keys. 
            // Assuming these keys might be missing in stats object, defaulting to 0 as in UI render
            if (['Denied Entry', 'In Progress', 'Incomplete', 'Withdrawn'].includes(g)) return 0;
            return stats[g] || 0;
        });
        const cRow = sheet.addRow(counts);
        cRow.eachCell(c => applyCellStyle(c));

        const percents = grades.map((g, i) => g === 'Total' ? '100%' : getPercent(counts[i]));
        const pRow = sheet.addRow(percents);
        pRow.eachCell(c => applyCellStyle(c, false, '#F3E8FF'));

        sheet.addRow([]);

        // Simplified Summary Table (A, B, C, D, F, Total)
        const simplifiedHeaders = ['A', 'B', 'C', 'D', 'F', 'Total'];
        const simplifiedCounts = {
            'A': (stats['A+'] || 0) + (stats['A'] || 0),
            'B': (stats['B+'] || 0) + (stats['B'] || 0),
            'C': (stats['C+'] || 0) + (stats['C'] || 0),
            'D': (stats['D+'] || 0) + (stats['D'] || 0),
            'F': stats['F'] || 0,
            'Total': total
        };

        const shRow = sheet.addRow(simplifiedHeaders);
        shRow.eachCell(c => applyHeaderStyle(c));

        const scRow = sheet.addRow(simplifiedHeaders.map(h => simplifiedCounts[h]));
        scRow.eachCell(c => applyCellStyle(c));

        // Simplified totals row font weight
        scRow.getCell(6).font = { bold: true };
    };

    const handleExportStudentGrades = async () => {
        if (!reportData) return;
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Student Grades');
        fillStudentGradesSheet(sheet);
        const buf = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buf]), `Student_Grades_${reportData.department.department_name_en}.xlsx`);
    };

    const fillCLOMeasurementSheet = (sheet) => {
        const domains = { 'Knowledge': ['K1', 'K2', 'K3', 'K4', 'K5'], 'Skills': ['S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8'], 'Values': ['V1', 'V2', 'V3', 'V4', 'V5'] };

        // Header Rows
        sheet.addRow(['Learning Outcomes', '', ...reportData.assessmentTools.map(t => t.name_en), 'Total', 'Actual', 'Target']).eachCell(c => applyHeaderStyle(c));
        sheet.addRow(['Domain', 'Code', ...reportData.assessmentTools.map(t => ''), '', '', '']).eachCell(c => applyHeaderStyle(c, false)); // Sub-header placeholder

        // Helper to calculate cell data (same as render logic)
        const calcCell = (type, tool) => {
            const targetQuestions = [];
            let toolMax = 0;
            tool.questions.forEach((q, idx) => {
                if (q.type === type) {
                    targetQuestions.push({ idx, q });
                    toolMax += q.max;
                }
            });

            if (targetQuestions.length === 0) return null;

            let scaledTotalAvg = 0;
            targetQuestions.forEach(({ q }) => {
                scaledTotalAvg += parseFloat(q.true_average || 0);
            });

            return { avg: scaledTotalAvg.toFixed(2), max: toolMax.toFixed(2) };
        };

        const calcRow = (type) => {
            let tMax = 0, tAvg = 0, tData = {};
            reportData.assessmentTools.forEach(t => {
                const d = calcCell(type, t);
                if (d) {
                    tData[t.key] = d;
                    tMax += parseFloat(d.max);
                    tAvg += parseFloat(d.avg);
                }
            });
            return { tData, tMax, tAvg: tAvg.toFixed(2), perc: tMax > 0 ? parseFloat(((tAvg / tMax) * 100).toFixed(2)) : 0 };
        };

        Object.entries(domains).forEach(([domain, types]) => {
            types.forEach((type, i) => {
                const { tData, tMax, tAvg, perc } = calcRow(type);
                const rowData = [
                    i === 0 ? domain : '', // Merge visual simulation
                    type,
                    ...reportData.assessmentTools.map(t => tData[t.key] ? `${tData[t.key].avg}/${tData[t.key].max}` : ''),
                    tMax > 0 ? `${tAvg}/${tMax.toFixed(2)}` : '',
                    tMax > 0 ? `${perc}%` : '',
                    tMax > 0 ? '70%' : ''
                ];
                sheet.addRow(rowData).eachCell((c, colN) => applyCellStyle(c, false));
            });
        });

        // Column widths
        sheet.getColumn(1).width = 15;
        sheet.getColumn(2).width = 10;
        reportData.assessmentTools.forEach((_, i) => sheet.getColumn(3 + i).width = 12);
    };

    const handleExportAll = async () => {
        if (!reportData) return;
        const workbook = new ExcelJS.Workbook();

        fillCLOMatrixSheet(workbook.addWorksheet('PLO Matrix'));
        fillCLOMeasurementSheet(workbook.addWorksheet('PLO Measurement'));
        fillStudentGradesSheet(workbook.addWorksheet('Student Grades'));
        fillMarksDistributionSheet(workbook.addWorksheet('Marks Distribution'));

        const buf = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buf]), `All_Reports_${reportData.department.department_name_en}.xlsx`);
    };
    const handlePrint = () => {
        window.print();
    };

    const handlePrintChart = () => {
        if (!chartRef.current) return;
        const canvas = chartRef.current.canvas;
        const dataUrl = canvas.toDataURL('image/png');
        const windowContent = `
            <!DOCTYPE html>
            <html dir="${isAr ? 'rtl' : 'ltr'}">
            <head>
                <title>${t('common.printChart')}</title>
                <style>
                    body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; font-family: sans-serif; }
                    .container { text-align: center; width: 90%; }
                    img { max-width: 100%; height: auto; border: 1px solid #ddd; padding: 10px; border-radius: 8px; }
                    h2 { margin-bottom: 20px; color: #1e293b; }
                    @media print {
                        .no-print { display: none; }
                        body { min-height: auto; }
                    }
                </style>
            </head>
            <body>
                <div class="container">
                    <h2>${t('common.printChart')}</h2>
                    <img src="${dataUrl}" alt="Chart" />
                </div>
                <script>
                    window.onload = () => {
                        window.print();
                        window.onafterprint = () => window.close();
                    };
                </script>
            </body>
            </html>
        `;
        const printWindow = window.open('', '_blank', 'width=1000,height=800');
        printWindow.document.write(windowContent);
        printWindow.document.close();
    };

    const renderMarksSection = () => {
        const cats = [
            { nameEn: 'Midterm I Exam', nameAr: 'الامتحان النصفي الأول', key: 'md1' },
            { nameEn: 'Midterm II Exam', nameAr: 'الامتحان النصفي الثاني', key: 'md2' },
            { nameEn: 'Lab Work', nameAr: 'العملي', key: 'lab' },
            { nameEn: 'Class Work (Quizzes, Assignments, etc.)', nameAr: 'أعمال الفصل (واجبات، كويزات، إلخ)', key: 'class' },
            { nameEn: 'Final Exam', nameAr: 'الامتحان النهائي', key: 'final' }
        ];

        return (
            <div className="report-section">
                <div className="section-header">
                    <h3>{isAr ? 'توزيع الدرجات' : 'Marks Distribution'}</h3>
                    <button className="btn btn-success no-print" onClick={handleExportMarksDistribution}>📥 Export</button>
                </div>
                <div className="table-responsive">
                    <table className="report-table excel-style-table">
                        <thead>
                            <tr>
                                <th rowSpan="2" style={{ width: '50px' }}>No.</th>
                                <th rowSpan="2" style={{ width: '120px' }}>{isAr ? 'رقم الطالب' : 'Student ID'}</th>
                                <th rowSpan="2" style={{ minWidth: '250px' }}>{isAr ? 'اسم الطالب' : 'Student Name'}</th>
                                {cats.map((col, idx) => (
                                    <th key={`col-${idx}`}>{isAr ? col.nameAr : col.nameEn}</th>
                                ))}
                                <th style={{ width: '80px' }}>{isAr ? 'المجموع' : 'Total'}</th>
                            </tr>
                            <tr>
                                {cats.map((col, idx) => (
                                    <th key={`max-${idx}`}>
                                        {col.key === 'class' ? +(getMaxByCategory('hm') + getMaxByCategory('qz')).toFixed(2) : getMaxByCategory(col.key)}
                                    </th>
                                ))}
                                <th style={{ fontWeight: 'bold' }}>
                                    {+(getMaxByCategory('md1') + getMaxByCategory('md2') + getMaxByCategory('lab') + getMaxByCategory('hm') + getMaxByCategory('qz') + getMaxByCategory('final')).toFixed(2)}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredStudents.map((student, idx) => (
                                <tr key={`marks-row-${student.student_code}`}>
                                    <td className="center-text">{idx + 1}</td>
                                    <td className="center-text">{student.student_code}</td>
                                    <td className="left-text px-15">
                                        {isAr ? student.full_name_ar : (student.full_name_en || student.full_name_ar)}
                                    </td>
                                    {cats.map((col, cIdx) => {
                                        const score = col.key === 'class' ? +(getCategoryScore(student, 'hm') + getCategoryScore(student, 'qz')).toFixed(2) : getCategoryScore(student, col.key);
                                        return <td key={`grade-${cIdx}`} className="center-text">{score.toFixed(2)}</td>;
                                    })}
                                    <td className="total-cell center-text">{getStudentTotal(student.student_code).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderStudentGradesSection = () => {
        const stats = reportData.gradeDistribution;
        const total = reportData.students.length;

        const studentStats = filteredStudents.map(student => {
            const hm = getCategoryScore(student, 'hm');
            const qz = getCategoryScore(student, 'qz');
            const md1 = getCategoryScore(student, 'md1');
            const md2 = getCategoryScore(student, 'md2');
            const lab = getCategoryScore(student, 'lab');
            // Per the table headers: Hm, Qz, Md1, Md2, Lab -> Semester Works -> Final -> Total
            const scores = [hm, qz, md1, md2, lab];
            const semesterWorks = scores.reduce((a, b) => a + b, 0);
            const final = getCategoryScore(student, 'final');
            const totalS = semesterWorks + final;

            // Calculate grade based on PERSONAL max percentage
            const pMax = student.total_personal_max || 100; // Fallback 100
            const pct = (totalS / pMax) * 100;
            const grade = calculateGrade(pct);

            return { ...student, scores, semesterWorks, final, totalS, grade };
        });

        const getPercent = (val) => total > 0 ? ((val / total) * 100).toFixed(0) + '%' : '0%';
        const successPercent = total > 0 ? ((stats.Pass / total) * 100).toFixed(2) : '0.00';

        const simplifiedCounts = {
            'A': (stats['A+'] || 0) + (stats['A'] || 0),
            'B': (stats['B+'] || 0) + (stats['B'] || 0),
            'C': (stats['C+'] || 0) + (stats['C'] || 0),
            'D': (stats['D+'] || 0) + (stats['D'] || 0),
            'F': stats['F'] || 0,
            'Total': total
        };

        return (
            <div className="report-section">
                <div className="section-header">
                    <h3>Student Grades & Statistics</h3>
                    <button className="btn btn-success no-print" onClick={handleExportStudentGrades}>📥 Export</button>
                </div>
                <div className="table-responsive">
                    <table className="report-table excel-style-table">
                        <thead>
                            <tr>
                                <th style={{ width: '120px' }}>Student ID</th>
                                <th style={{ minWidth: '250px' }}>Student Name</th>
                                <th style={{ width: '50px' }}>Hm</th>
                                <th style={{ width: '50px' }}>Qz</th>
                                <th style={{ width: '50px' }}>Md1</th>
                                <th style={{ width: '50px' }}>Md2</th>
                                <th style={{ width: '50px' }}>Lab</th>
                                <th style={{ width: '80px', backgroundColor: '#e2e8f0', color: '#000' }}>Semester Works</th>
                                <th style={{ width: '60px' }}>Final</th>
                                <th style={{ width: '60px', fontWeight: 'bold' }}>Total</th>
                                <th style={{ width: '60px' }}>Grade</th>
                            </tr>
                        </thead>
                        <tbody>
                            {studentStats.map((s) => (
                                <tr key={`grades-row-${s.student_code}`} className={s.grade === 'F' ? 'row-fail' : ''}>
                                    <td className="center-text">{s.student_code}</td>
                                    <td className="left-text px-15">
                                        {isAr ? s.full_name_ar : (s.full_name_en || s.full_name_ar)}
                                    </td>
                                    <td className="center-text">{s.scores[0] || 0}</td>
                                    <td className="center-text">{s.scores[1] || 0}</td>
                                    <td className="center-text">{s.scores[2] || 0}</td>
                                    <td className="center-text">{s.scores[3] || 0}</td>
                                    <td className="center-text">{s.scores[4] || 0}</td>
                                    <td className="center-text" style={{ backgroundColor: '#f8fafc', fontWeight: 'bold' }}>{s.semesterWorks.toFixed(2)}</td>
                                    <td className="center-text">{s.final || 0}</td>
                                    <td className="center-text total-cell">{s.totalS.toFixed(2)}</td>
                                    <td className={`center-text grade-cell grade-${s.grade}`} style={{ fontWeight: 'bold' }}>{s.grade}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Course Report Section */}
                <div style={{ marginTop: '30px' }}>
                    <div className="course-report-flex" style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Succ %</div>
                            <div style={{ padding: '10px', background: '#dcfce7', borderRadius: '4px', fontWeight: 'bold' }}>{successPercent}%</div>
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ textAlign: 'center', fontWeight: 'bold', background: '#f8fafc', padding: '5px', border: '1px solid #000' }}>Course Report</div>
                            <div className="table-responsive">
                                <table className="excel-style-table" style={{ width: '100%', marginTop: '-1px' }}>
                                    <thead>
                                        <tr>
                                            <th>A+</th><th>A</th><th>B+</th><th>B</th><th>C+</th><th>C</th><th>D+</th><th>D</th><th>F</th>
                                            <th style={{ fontSize: '0.8rem' }}>Denied Entry</th>
                                            <th style={{ fontSize: '0.8rem' }}>In Progress</th>
                                            <th style={{ fontSize: '0.8rem' }}>Incomplete</th>
                                            <th>Pass</th><th>Fail</th><th>Withdrawn</th><th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="center-text">{stats['A+'] || 0}</td>
                                            <td className="center-text">{stats['A'] || 0}</td>
                                            <td className="center-text">{stats['B+'] || 0}</td>
                                            <td className="center-text">{stats['B'] || 0}</td>
                                            <td className="center-text">{stats['C+'] || 0}</td>
                                            <td className="center-text">{stats['C'] || 0}</td>
                                            <td className="center-text">{stats['D+'] || 0}</td>
                                            <td className="center-text">{stats['D'] || 0}</td>
                                            <td className="center-text">{stats['F'] || 0}</td>
                                            <td className="center-text">0</td><td className="center-text">0</td><td className="center-text">0</td>
                                            <td className="center-text">{stats['Pass'] || 0}</td>
                                            <td className="center-text">{stats['Fail'] || 0}</td>
                                            <td className="center-text">0</td>
                                            <td className="center-text" style={{ fontWeight: 'bold' }}>{total}</td>
                                        </tr>
                                        <tr>
                                            <td className="center-text text-purple">{getPercent(stats['A+'] || 0)}</td>
                                            <td className="center-text text-purple">{getPercent(stats['A'] || 0)}</td>
                                            <td className="center-text text-purple">{getPercent(stats['B+'] || 0)}</td>
                                            <td className="center-text text-purple">{getPercent(stats['B'] || 0)}</td>
                                            <td className="center-text text-purple">{getPercent(stats['C+'] || 0)}</td>
                                            <td className="center-text text-purple">{getPercent(stats['C'] || 0)}</td>
                                            <td className="center-text text-purple">{getPercent(stats['D+'] || 0)}</td>
                                            <td className="center-text text-purple">{getPercent(stats['D'] || 0)}</td>
                                            <td className="center-text text-purple">{getPercent(stats['F'] || 0)}</td>
                                            <td className="center-text text-purple">0%</td><td className="center-text text-purple">0%</td><td className="center-text text-purple">0%</td>
                                            <td className="center-text text-purple">{getPercent(stats['Pass'] || 0)}</td>
                                            <td className="center-text text-purple">{getPercent(stats['Fail'] || 0)}</td>
                                            <td className="center-text text-purple">0%</td>
                                            <td className="center-text text-purple">100%</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Simplified Summary */}
                            <div className="table-responsive">
                                <table className="excel-style-table" style={{ width: '300px', marginTop: '10px' }}>
                                    <thead>
                                        <tr><th>A</th><th>B</th><th>C</th><th>D</th><th>F</th><th>Total</th></tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="center-text">{simplifiedCounts['A']}</td>
                                            <td className="center-text">{simplifiedCounts['B']}</td>
                                            <td className="center-text">{simplifiedCounts['C']}</td>
                                            <td className="center-text">{simplifiedCounts['D']}</td>
                                            <td className="center-text">{simplifiedCounts['F']}</td>
                                            <td className="center-text" style={{ fontWeight: 'bold' }}>{simplifiedCounts['Total']}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) return <div className="loading-container"><div className="loading-spinner"></div></div>;

    return (
        <div className="clos-page fade-in dept-report-page" dir={isAr ? 'rtl' : 'ltr'}>
            <style>{`
                @media print {
                    .clos-header, .no-print, .header-actions { display: none !important; }
                    .dept-report-page { padding: 0 !important; margin: 0 !important; background: white !important; }
                    .report-section { page-break-inside: avoid; margin-bottom: 40px !important; border: 1px solid #000; padding: 20px; box-shadow: none !important; }
                    .sticky-col { position: static !important; background: white !important; border: 1px solid #000 !important; }
                    .report-table, .matrix-table, .clo-form-table { width: 100% !important; font-size: 8pt !important; border-collapse: collapse !important; }
                    .matrix-container { overflow: visible !important; height: auto !important; max-height: none !important; }
                    body { background: white !important; }
                    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                }
                @media (max-width: 768px) {
                    .sticky-col { 
                        position: static !important; 
                        left: auto !important;
                        right: auto !important;
                        width: auto !important; 
                        min-width: auto !important; 
                        background: white !important;
                    }
                    .sticky-col-idx,
                    .sticky-col-id,
                    .sticky-col-name,
                    .sticky-col-last {
                        position: static !important;
                        left: auto !important;
                        right: auto !important;
                    }
                    .matrix-container {
                        overflow-x: auto !important;
                        -webkit-overflow-scrolling: touch;
                    }
                    .table-responsive {
                        overflow-x: auto !important;
                        -webkit-overflow-scrolling: touch;
                    }
                    .course-report-flex {
                        flex-direction: column !important;
                        align-items: center !important;
                    }
                    .course-report-flex > div {
                        width: 100% !important;
                        margin-bottom: 10px;
                    }
                }
                .matrix-table { width: 100%; font-size: 0.75rem; border-collapse: collapse; }
                .matrix-table th, .matrix-table td { padding: 6px 4px !important; text-align: center; border: 1px solid #e2e8f0; }
                .matrix-container { overflow-x: auto; max-width: 100%; margin-bottom: 20px; border: 1px solid #e2e8f0; border-radius: 8px; }
                /* No-scroll mode for department reports */
                .report-content-container .matrix-container { max-height: none !important; overflow: auto !important; }
                .report-section { background: white; border-radius: 12px; border: 1px solid #e2e8f0; padding: 16px; margin-bottom: 32px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
                .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; border-bottom: 2px solid #f1f5f9; padding-bottom: 8px; }
                .section-header h3 { margin: 0; color: #1e293b; font-size: 1.1rem; }
                .stats-container-horizontal { margin-top: 16px; display: flex; gap: 16px; align-items: flex-start; }
                .stat-card-mini { text-align: center; background: #f8fafc; padding: 12px; border: 2px solid #1e293b; border-radius: 8px; min-width: 110px; }
                .stat-val { fontSize: 1.5rem; color: #7c3aed; font-weight: bold; }
                .stats-table { flex: 1; overflow-x: auto; }
                .dept-report-page { padding: 20px; background: #f8fafc; min-height: 100vh; }
                .excel-style-table { font-size: 0.75rem !important; }
                .excel-style-table th, .excel-style-table td { padding: 4px !important; }
                .no-print { transition: transform 0.2s; }
                .no-print:hover { transform: translateY(-1px); }
            `}</style>

            <div className="clos-header no-print">
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flex: 1 }}>
                    <h1 style={{ margin: 0 }}>{t('admin.departmentReports.title')}</h1>
                    <select className="header-select" value={selectedDeptId} onChange={(e) => setSelectedDeptId(e.target.value)}>
                        <option value="">-- {isAr ? 'اختر القسم' : 'Select Dept'} --</option>
                        {departments.map(d => <option key={`dept-opt-${d.department_id}`} value={d.department_id}>{isAr ? d.department_name_ar : d.department_name_en}</option>)}
                    </select>
                </div>
            </div>

            {
                fetching ? (
                    <div className="loading-container"><div className="loading-spinner"></div></div>
                ) : reportData ? (
                    <div className="report-content-container">
                        <div className="report-info-banner no-print" style={{ marginBottom: '24px', background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', gap: '40px' }}>
                            <div>
                                <strong>{isAr ? 'القسم: ' : 'Dept: '}</strong> {isAr ? reportData.department.department_name_ar : reportData.department.department_name_en}
                            </div>
                            <div>
                                <strong>{isAr ? 'عدد الطلاب: ' : 'Students: '}</strong> {reportData.students.length}
                            </div>
                        </div>

                        {/* Department Courses Grid */}
                        {deptCourses.length > 0 && (
                            <div className="courses-grid no-print" style={{ marginBottom: '24px' }}>
                                {deptCourses.map(course => (
                                    <div key={course.course_id} className={"course-card" + (selectedCourseId === course.course_id ? ' selected' : '')} onClick={() => handleCourseClick(course.course_id)} style={{ cursor: 'pointer', border: selectedCourseId === course.course_id ? '2px solid var(--primary)' : '1px solid #e2e8f0' }}>
                                        <h3 style={{ margin: '0 0 4px' }}>{course.course_code}</h3>
                                        <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>{isAr ? course.course_name_ar : course.course_name_en}</p>
                                        <p style={{ margin: '4px 0', fontSize: '0.85rem', color: '#64748b' }}>
                                            <span className="material-icons" style={{ fontSize: '14px', verticalAlign: 'middle', marginInlineEnd: '4px' }}>person</span>
                                            {isAr ? course.teacher_name_ar : (course.teacher_name_en || course.teacher_name_ar)}
                                        </p>
                                        <p className="dept-text" style={{ margin: 0 }}>{course.target_gender === 'male' ? (isAr ? 'قسم الطلاب' : 'Male') :
                                            course.target_gender === 'female' ? (isAr ? 'قسم الطالبات' : 'Female') : (isAr ? 'مشترك' : 'Both')}</p>
                                        <span className="badge gender-badge">{course.students_count} {t('common.students')}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {/* Main Overview Matrix */}
                        {selectedCourseId && (
                            <div className="report-section" >
                                <div className="section-header">
                                    <h3>{isAr ? 'مصفوفة نواتج التعلم المجمعة' : 'Aggregated PLO Matrix'}</h3>
                                    <button className="btn btn-success no-print" onClick={handleExportCLOMatrix}>📥 Export</button>
                                </div>
                                <div className="table-responsive">
                                    <table className="report-table excel-style-table">
                                        <thead>
                                            {/* Row 1: Assessment Names */}
                                            <tr>
                                                <th rowSpan="4" style={{ width: '50px' }}>#</th>
                                                <th rowSpan="4" style={{ width: '120px' }}>{isAr ? 'رقم الطالب' : 'ID'}</th>
                                                <th className="header-label-cell" style={{ minWidth: '250px' }}>
                                                    {isAr ? 'أداة التقييم' : 'Assessment Tool'}
                                                </th>
                                                {assessmentTools.flatMap(t => t.questions.map((q, i) => (
                                                    <th key={`${t.key}-${i}`}>
                                                        {isAr ? (t.name_ar || t.name_en || t.tool_name_ar || t.tool_name_en) : (t.name_en || t.tool_name_en)}
                                                    </th>
                                                )))}
                                                <th style={{ width: '80px' }}>{isAr ? 'المجموع' : 'Total'}</th>
                                            </tr>
                                            {/* Row 3: Max Scores */}
                                            <tr className="header-row-max">
                                                <th className="header-label-cell">
                                                    {isAr ? 'الدرجة' : 'Max'}
                                                </th>
                                                {assessmentTools.flatMap(t => t.questions.map((q, i) => (
                                                    <th key={`max-${t.key}-${i}`} className="max-score-header">
                                                        {q.max.toFixed(2)}
                                                    </th>
                                                )))}
                                                <th className="max-score-header">
                                                    {assessmentTools.reduce((acc, t) => acc + (t.questions || []).reduce((s, q) => s + q.max, 0), 0).toFixed(2)}
                                                </th>
                                            </tr>
                                            {/* Row 4: Averages */}
                                            <tr className="average-row">
                                                <th className="header-label-cell" style={{ textAlign: 'center' }}>
                                                    {isAr ? 'المتوسط' : 'Average'}
                                                </th>
                                                {assessmentTools.flatMap(t => t.questions.map((q, i) => (
                                                    <th key={`avg-${t.key}-${i}`} className="average-header-cell">
                                                        {getQuestionAverage(t.key, i, q.question_id).toFixed(2)}
                                                    </th>
                                                )))}
                                                <th className="average-header-cell">
                                                    {assessmentTools.reduce((acc, t) => acc + (t.questions || []).reduce((s, q, i) => s + getQuestionAverage(t.key, i, q.question_id), 0), 0).toFixed(2)}
                                                </th>
                                            </tr>
                                            {/* Row 5: PLOs */}
                                            <tr className="header-row-clos">
                                                <th className="header-label-cell">
                                                    {isAr ? 'مخرجات التعلم' : 'PLOs'}
                                                </th>
                                                {assessmentTools.flatMap(t => t.questions.map((q, i) => (
                                                    <th key={`clo-${t.key}-${i}`} className="clo-label-cell">
                                                        {q.type}
                                                    </th>
                                                )))}
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredStudents.map((s, i) => (
                                                <tr key={s.student_code}>
                                                    <td className="center-text">{i + 1}</td>
                                                    <td className="center-text">{s.student_code}</td>
                                                    <td className="left-text px-15">{isAr ? s.full_name_ar : (s.full_name_en || s.full_name_ar)}</td>
                                                    {assessmentTools.flatMap(t => t.questions.map((q, j) => {
                                                        const score = selectedCourseId && t.gradesByStudent
                                                            ? t.gradesByStudent[s.student_id]?.[q.question_id]
                                                            : s.tools?.[t.key]?.[j];
                                                        return (
                                                            <td key={`${s.student_code}-${t.key}-${j}`} className="center-text">
                                                                {score !== undefined && score !== null && score !== '' ? parseFloat(score).toFixed(2) : '0.00'}
                                                            </td>
                                                        );
                                                    }))}
                                                    <td className="total-cell center-text">{assessmentTools.reduce((total, t) => total + (t.questions || []).reduce((toolTotal, q, j) => {
                                                        const score = selectedCourseId && t.gradesByStudent ? t.gradesByStudent[s.student_id]?.[q.question_id] : s.tools?.[t.key]?.[j];
                                                        return toolTotal + (parseFloat(score) || 0);
                                                    }, 0), 0).toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}


                        {(() => {
                            const domains = { 'Knowledge': ['K1', 'K2', 'K3', 'K4', 'K5'], 'Skills': ['S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8'], 'Values': ['V1', 'V2', 'V3', 'V4', 'V5'] };

                            const calcCell = (type, tool) => {
                                // Find indices of questions with this type in this tool
                                const targetQuestions = [];
                                let toolMax = 0;

                                tool.questions.forEach((q, idx) => {
                                    if (q.type === type) {
                                        targetQuestions.push({ idx, q });
                                        toolMax += q.max;
                                    }
                                });

                                if (targetQuestions.length === 0) return null;

                                let scaledTotalAvg = 0;
                                targetQuestions.forEach(({ q }) => {
                                    scaledTotalAvg += parseFloat(q.true_average || 0);
                                });

                                return { avg: scaledTotalAvg.toFixed(2), max: toolMax.toFixed(2) };
                            };

                            const calcRow = (type) => {
                                let tMax = 0, tAvg = 0, tData = {};
                                reportData.assessmentTools.forEach(t => {
                                    const d = calcCell(type, t);
                                    if (d) {
                                        tData[t.key] = d;
                                        tMax += parseFloat(d.max);
                                        tAvg += parseFloat(d.avg);
                                    }
                                });
                                return { tData, tMax, tAvg: tAvg.toFixed(2), perc: tMax > 0 ? parseFloat(((tAvg / tMax) * 100).toFixed(2)) : 0 };
                            };

                            return (
                                <div className="report-section clo-print-section">
                                    <div className="section-header">
                                        <h3>{isAr ? 'نموذج قياس مخرجات التعلم' : 'PLOs Measurement Form'}</h3>
                                    </div>
                                    <div className="table-responsive">
                                        <table className="clo-form-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }}>
                                            <thead>
                                                <tr className="bg-blue-light"><th colSpan="2">Learning Outcomes</th><th colSpan={reportData.assessmentTools.length + 1}>Assessment methods</th><th colSpan="3">PLOs</th></tr>
                                                <tr className="bg-blue-light">
                                                    <th>Domain</th>
                                                    <th>Code</th>
                                                    {reportData.assessmentTools.map(t => <th key={t.key}>{t.name_en}</th>)}
                                                    <th>Total</th><th>Actual</th><th>Target</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(domains).map(([domain, types]) => types.map((type, i) => {
                                                    const { tData, tMax, tAvg, perc } = calcRow(type);
                                                    return (
                                                        <tr key={type}>
                                                            {i === 0 && <td rowSpan={types.length} className="domain-rspan">{domain}</td>}
                                                            <td style={{ textAlign: 'left', paddingLeft: '10px' }}>{type} <input type="checkbox" checked={tMax > 0} readOnly /></td>
                                                            {reportData.assessmentTools.map(t => (
                                                                <td key={t.key}>
                                                                    {tData[t.key] ? `${tData[t.key].avg}/${tData[t.key].max}` : ''}
                                                                </td>
                                                            ))}
                                                            <td>{tMax > 0 ? `${tAvg}/${tMax.toFixed(2)}` : ''}</td>
                                                            <td>{tMax > 0 ? `${perc.toFixed(2)}%` : ''}</td>
                                                            <td>{tMax > 0 ? '70%' : ''}</td>
                                                        </tr>
                                                    );
                                                }))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Marks Distribution section removed */}
                        {/* Student Grades section removed */}

                        {/* PLO Achievement Column Chart */}
                        {cloChartData && (
                            <div className="report-section">
                                <div className="section-header">
                                    <h3>{isAr ? 'مخطط نسبة تحقيق PLOs' : 'PLOs Achievement Chart'}</h3>
                                    <button className="btn btn-outline no-print" onClick={handlePrintChart}>
                                        <span className="material-icons" style={{ fontSize: '18px', verticalAlign: 'middle', marginInlineEnd: '5px' }}>print</span>
                                        {t('common.printChart')}
                                    </button>
                                </div>
                                <div style={{ maxWidth: '100%', padding: '10px' }}>
                                    <Bar ref={chartRef} data={cloChartData} options={{
                                        responsive: true,
                                        plugins: { legend: { display: false } },
                                        scales: { y: { beginAtZero: true, max: 100 } },
                                        plugins: {
                                            annotation: {
                                                annotations: {
                                                    targetLine: {
                                                        type: 'line',
                                                        yMin: 70,
                                                        yMax: 70,
                                                        borderColor: 'red',
                                                        borderWidth: 2,
                                                        label: {
                                                            content: 'Target',
                                                            enabled: true,
                                                            position: 'start',
                                                            backgroundColor: 'rgba(255,0,0,0.1)',
                                                            color: 'red'
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }} />
                                </div>
                            </div>
                        )}

                        <div className="no-print" style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                            <p>{isAr ? 'نهاية التقرير المجمع' : 'End of Aggregated Report'}</p>
                        </div>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '100px', background: 'white', borderRadius: '16px', border: '1px dashed var(--border)' }}>
                        <span className="material-icons" style={{ fontSize: '64px', color: '#ccc' }}>bar_chart</span>
                        <h3 style={{ color: '#888' }}>{isAr ? 'برجاء اختيار القسم لعرض التقارير المجمعة' : 'Please select a department to view aggregated metrics'}</h3>
                    </div>
                )
            }
        </div >
    );
};
export default DepartmentReports;
